import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ChartViewAdapter } from '../lib/playcanvas-harness/chart-view-adapter'
import { redactSensitiveUrl } from '../lib/playcanvas-harness/safe-diagnostics'
import type { ScreenCorners } from '../lib/playcanvas-harness/types'
import { DEVICE_SHAPES, FRAMEBUFFER_SCALES, type DeviceShape } from './device-shapes'
import './playcanvas-chart-harness.css'

// Offline licensed-chart route used for gesture testing without tournament
// credentials or a WebSocket connection.
const DEFAULT_CHART_BASE_URL = `${window.location.origin}/`
const DEFAULT_DUMMY_AAPL_URL = `${window.location.origin}/?dummyAapl=true`

const DEFAULT_TOURNAMENT_ID = 'harness-tournament'
const DEFAULT_PLAYER_ID = 'harness-player'
const DEFAULT_TICKET = 'harness-ticket-not-persisted'
const DEFAULT_WS_BASE_URL = 'ws://localhost:8080'
const DEFAULT_SYMBOL = 'AAPL'

/**
 * Computes the four framebuffer-space screen corners for a placeholder
 * rectangle expressed in CSS pixels relative to the canvas, matching the
 * coordinate semantics `ChartView` expects: corners[0] is bottom-left,
 * corners[2] is top-right, in framebuffer pixels with Y flipped (0 = bottom).
 */
function toScreenCorners(
  placeholderCssRect: { left: number; top: number; width: number; height: number },
  canvasCssSize: { width: number; height: number },
  framebufferScale: number
): ScreenCorners {
  const fbLeft = placeholderCssRect.left * framebufferScale
  const fbRight = (placeholderCssRect.left + placeholderCssRect.width) * framebufferScale
  const fbTop = placeholderCssRect.top * framebufferScale
  const fbBottom = (placeholderCssRect.top + placeholderCssRect.height) * framebufferScale
  const fbCanvasHeight = canvasCssSize.height * framebufferScale

  // Flip Y: framebuffer Y=0 is the bottom of the canvas.
  const bottomLeftY = fbCanvasHeight - fbBottom
  const topRightY = fbCanvasHeight - fbTop

  return [
    { x: fbLeft, y: bottomLeftY },
    { x: fbLeft, y: topRightY },
    { x: fbRight, y: topRightY },
    { x: fbRight, y: bottomLeftY },
  ]
}

export function PlayCanvasChartHarness(): React.ReactElement {
  const [deviceShape, setDeviceShape] = useState<DeviceShape>(DEVICE_SHAPES[1])
  const [framebufferScale, setFramebufferScale] = useState<number>(1)
  const [placeholderRect, setPlaceholderRect] = useState(DEVICE_SHAPES[1].placeholderRect)
  const [placeholderVisible, setPlaceholderVisible] = useState(true)
  const [symbol, setSymbol] = useState(DEFAULT_SYMBOL)
  const [chartUrlOverride, setChartUrlOverride] = useState('')
  const [chartUrlInput, setChartUrlInput] = useState('')
  const [canvasOnlyFullscreen, setCanvasOnlyFullscreen] = useState(false)
  const [wrapperFullscreen, setWrapperFullscreen] = useState(false)
  const [statusLog, setStatusLog] = useState<string[]>([])
  const [outsideTouchCount, setOutsideTouchCount] = useState(0)

  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const chartViewRef = useRef<ChartViewAdapter | null>(null)

  const appendLog = useCallback((message: string) => {
    setStatusLog((prev) => [...prev.slice(-19), `${new Date().toLocaleTimeString()} ${message}`])
  }, [])

  // Keep the mock chartPlaceholder contract in sync with harness controls.
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    if (!placeholderVisible) {
      window.chartPlaceholder = { element: undefined }
      return
    }

    const corners = toScreenCorners(
      placeholderRect,
      { width: canvas.clientWidth, height: canvas.clientHeight },
      framebufferScale
    )

    window.chartPlaceholder = {
      element: { screenCorners: corners },
    }
  }, [placeholderRect, placeholderVisible, framebufferScale, deviceShape])

  // Keep canvas framebuffer resolution matching the configured CSS size and scale.
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    canvas.width = deviceShape.width * framebufferScale
    canvas.height = deviceShape.height * framebufferScale
  }, [deviceShape, framebufferScale])

  const startChartView = useCallback(() => {
    chartViewRef.current?.destroy()

    const adapter = new ChartViewAdapter({
      chartBaseUrl: DEFAULT_CHART_BASE_URL,
      fullIframeUrlOverride: chartUrlOverride || DEFAULT_DUMMY_AAPL_URL,
      logger: {
        warn: (message, details) => appendLog(`WARN ${message} ${details ? JSON.stringify(details) : ''}`),
        log: (message, details) => appendLog(`${message} ${details ?? ''}`),
      },
    })

    adapter.show(symbol, DEFAULT_TOURNAMENT_ID, DEFAULT_PLAYER_ID, DEFAULT_TICKET, DEFAULT_WS_BASE_URL)

    chartViewRef.current = adapter
    appendLog('ChartView started')
  }, [chartUrlOverride, symbol, appendLog])

  const destroyChartView = useCallback(() => {
    chartViewRef.current?.destroy()
    chartViewRef.current = null
    appendLog('ChartView destroyed')
  }, [appendLog])

  useEffect(() => {
    startChartView()
    return () => {
      chartViewRef.current?.destroy()
      chartViewRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    chartViewRef.current?.setSymbol(symbol)
  }, [symbol])

  const currentIframeUrl = useMemo(() => {
    const iframe = document.querySelector('iframe')
    return iframe ? redactSensitiveUrl(iframe.src) : ''
    // statusLog is a proxy trigger: the iframe URL can change (symbol updates, recreation) whenever a log line is appended.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusLog])

  const enterCanvasOnlyFullscreen = useCallback(async () => {
    if (typeof canvasRef.current?.requestFullscreen !== 'function') {
      appendLog('Canvas-only fullscreen unsupported in this browser')
      return
    }

    try {
      await canvasRef.current.requestFullscreen()
      setCanvasOnlyFullscreen(true)
      appendLog('Canvas-only fullscreen requested')
    } catch (err) {
      appendLog(`Canvas-only fullscreen failed: ${String(err)}`)
    }
  }, [appendLog])

  const enterWrapperFullscreen = useCallback(async () => {
    if (typeof wrapperRef.current?.requestFullscreen !== 'function') {
      appendLog('Shared-wrapper fullscreen unsupported in this browser')
      return
    }

    // ChartView always appends the iframe to document.body, outside any wrapper. A wrapper
    // becoming the fullscreen element only keeps the iframe visible if something actually
    // moves it inside that wrapper first — the harness performs that relocation here since
    // the real ChartView has no such step.
    const iframe = document.querySelector('iframe')
    const wasRelocated = iframe && wrapperRef.current && !wrapperRef.current.contains(iframe)
    if (wasRelocated) {
      wrapperRef.current!.appendChild(iframe!)
    }

    try {
      await wrapperRef.current.requestFullscreen()
      setWrapperFullscreen(true)
      appendLog('Shared-wrapper fullscreen requested (iframe relocated into wrapper)')
    } catch (err) {
      // The request was rejected (or unsupported despite the method existing): undo the
      // relocation so the iframe isn't left stranded inside the wrapper with fullscreen
      // status still reporting "not fullscreen".
      if (wasRelocated && iframe && iframe.parentElement !== document.body) {
        document.body.appendChild(iframe)
      }
      setWrapperFullscreen(false)
      appendLog(`Shared-wrapper fullscreen failed: ${String(err)}`)
    }
  }, [appendLog])

  const exitFullscreen = useCallback(async () => {
    if (document.fullscreenElement) {
      await document.exitFullscreen()
    }
    // The fullscreenchange listener below performs the actual iframe relocation and state
    // sync, so this also covers Escape / browser-chrome exits, not just this button.
  }, [])

  // Native fullscreen exits (Escape key, browser back/close-fullscreen control) bypass
  // exitFullscreen() entirely. Listening for fullscreenchange, rather than relying only on
  // the button handler, keeps the iframe location and the displayed status correct no matter
  // how fullscreen was left.
  useEffect(() => {
    const handleFullscreenChange = () => {
      if (document.fullscreenElement) return

      const iframe = document.querySelector('iframe')
      if (iframe && iframe.parentElement !== document.body) {
        document.body.appendChild(iframe)
      }

      setCanvasOnlyFullscreen(false)
      setWrapperFullscreen(false)
      appendLog('Exited fullscreen (fullscreenchange)')
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange)
  }, [appendLog])

  const applyChartUrlOverride = useCallback(() => {
    setChartUrlOverride(chartUrlInput.trim())
    appendLog('Full iframe URL override applied for this session (not persisted) — recreating ChartView')
  }, [chartUrlInput, appendLog])

  // Recreate the ChartView whenever the override changes so an applied authenticated
  // URL takes effect immediately instead of waiting for a manual "Recreate ChartView" click.
  useEffect(() => {
    if (chartViewRef.current) {
      startChartView()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartUrlOverride])

  return (
    <div className="pc-harness">
      <div className="pc-harness__controls">
        <h1>PlayCanvas Chart Iframe Harness (dev only)</h1>

        <section>
          <h2>Device shape</h2>
          {DEVICE_SHAPES.map((shape) => (
            <label key={shape.id} className="pc-harness__radio">
              <input
                type="radio"
                name="device-shape"
                checked={deviceShape.id === shape.id}
                onChange={() => {
                  setDeviceShape(shape)
                  setPlaceholderRect(shape.placeholderRect)
                }}
              />
              {shape.label}
            </label>
          ))}
        </section>

        <section>
          <h2>Framebuffer scale</h2>
          {FRAMEBUFFER_SCALES.map((scale) => (
            <label key={scale} className="pc-harness__radio">
              <input
                type="radio"
                name="fb-scale"
                checked={framebufferScale === scale}
                onChange={() => setFramebufferScale(scale)}
              />
              {scale}x
            </label>
          ))}
        </section>

        <section>
          <h2>Placeholder</h2>
          <label>
            Left
            <input
              type="number"
              value={placeholderRect.left}
              onChange={(e) => setPlaceholderRect((r) => ({ ...r, left: Number(e.target.value) }))}
            />
          </label>
          <label>
            Top
            <input
              type="number"
              value={placeholderRect.top}
              onChange={(e) => setPlaceholderRect((r) => ({ ...r, top: Number(e.target.value) }))}
            />
          </label>
          <label>
            Width
            <input
              type="number"
              value={placeholderRect.width}
              onChange={(e) => setPlaceholderRect((r) => ({ ...r, width: Number(e.target.value) }))}
            />
          </label>
          <label>
            Height
            <input
              type="number"
              value={placeholderRect.height}
              onChange={(e) => setPlaceholderRect((r) => ({ ...r, height: Number(e.target.value) }))}
            />
          </label>
          <button onClick={() => setPlaceholderVisible((v) => !v)}>
            {placeholderVisible ? 'Hide placeholder (simulate loss)' : 'Restore placeholder'}
          </button>
        </section>

        <section>
          <h2>Symbol</h2>
          <input value={symbol} onChange={(e) => setSymbol(e.target.value)} />
        </section>

        <section>
          <h2>Chart source</h2>
          <p className="pc-harness__hint">
            Default: locally served licensed chart with offline dummy AAPL data. It needs no tournament WebSocket,
            ticket, or backend and supports touch/pan/pinch checks. Optionally paste a complete authenticated
            tournament iframe URL below; it is loaded exactly as given and never persisted.
          </p>
          <input
            placeholder="https://example.com/?tournament=true&tournamentId=...&ticket=..."
            value={chartUrlInput}
            onChange={(e) => setChartUrlInput(e.target.value)}
          />
          <button onClick={applyChartUrlOverride}>Apply full URL override</button>
          {chartUrlOverride && <button onClick={() => setChartUrlOverride('')}>Clear override (use dummy AAPL)</button>}
        </section>

        <section>
          <h2>Fullscreen</h2>
          <button onClick={enterCanvasOnlyFullscreen}>Canvas-only fullscreen</button>
          <button onClick={enterWrapperFullscreen}>Shared-wrapper fullscreen</button>
          <button onClick={exitFullscreen}>Exit fullscreen</button>
          <p className="pc-harness__status">
            Status: {canvasOnlyFullscreen ? 'canvas-only' : wrapperFullscreen ? 'wrapper' : 'not fullscreen'}
          </p>
        </section>

        <section>
          <h2>Lifecycle</h2>
          <button onClick={startChartView}>Recreate ChartView</button>
          <button onClick={destroyChartView}>Destroy ChartView</button>
        </section>

        <section>
          <h2>Input ownership</h2>
          <p className="pc-harness__hint">
            The real iframe swallows pointer events before they reach this page, so a gesture starting inside the
            placeholder cannot be counted here (only content the iframe itself instruments could report that). Judge
            "inside" ownership by the chart visibly reacting; judge "outside" ownership by this counter increasing
            while the chart does not react.
          </p>
          <p>Outside-canvas touches: {outsideTouchCount}</p>
        </section>

        <section>
          <h2>Diagnostics</h2>
          <p className="pc-harness__hint">Current iframe URL (redacted): {currentIframeUrl || '(none)'}</p>
          <div className="pc-harness__log">
            {statusLog.map((line, i) => (
              <div key={i}>{line}</div>
            ))}
          </div>
        </section>
      </div>

      <div
        ref={wrapperRef}
        className="pc-harness__stage"
        onPointerDown={() => setOutsideTouchCount((c) => c + 1)}
      >
        <div
          className="pc-harness__device"
          style={{ width: deviceShape.width, height: deviceShape.height }}
        >
          <canvas
            ref={canvasRef}
            className="pc-harness__canvas"
            style={{ width: deviceShape.width, height: deviceShape.height }}
          />
          {placeholderVisible && (
            <div
              className="pc-harness__placeholder"
              style={{
                left: placeholderRect.left,
                top: placeholderRect.top,
                width: placeholderRect.width,
                height: placeholderRect.height,
              }}
            />
          )}
        </div>
      </div>
    </div>
  )
}
